import { reactive, readonly } from 'vue';
import {
  localStorageBroadcastChannelName,
  type LocalStorageBroadcastMessage,
} from './engineTypes';
import { tabStorageRuntimeEpoch } from './storageRuntimeEpoch';

export const storageUpdateReloadPrefix = 'alfrec.web.storage-update-reload.';

export type StorageUpdatePhase = 'idle' | 'reload-required' | 'stale-bundle';

export type StorageUpdateView = {
  phase: StorageUpdatePhase;
  requiredEpoch?: number;
  title?: string;
  message?: string;
};

export const storageUpdateReloadKey = (requiredEpoch: number) =>
  `${storageUpdateReloadPrefix}${requiredEpoch}`;

export const idleStorageUpdate = (): StorageUpdateView => ({ phase: 'idle' });

export const isBlockingStorageUpdatePhase = (phase: StorageUpdatePhase) =>
  phase === 'reload-required' || phase === 'stale-bundle';

export const storageUpdateForRequiredEpoch = (
  currentEpoch: number,
  requiredEpoch: number,
  alreadyAttemptedReload: boolean,
): StorageUpdateView => {
  if (!Number.isSafeInteger(requiredEpoch) || requiredEpoch <= currentEpoch) {
    return idleStorageUpdate();
  }
  if (alreadyAttemptedReload) {
    return {
      phase: 'stale-bundle',
      requiredEpoch,
      title: 'Reload this tab',
      message:
        'This tab is still running an older Alfrec storage runtime. Reload or close it so the newer tab can use local storage.',
    };
  }
  return {
    phase: 'reload-required',
    requiredEpoch,
    title: 'Reload Alfrec',
    message:
      'A newer Alfrec storage runtime is ready. Reload this tab to continue.',
  };
};

export const readAttemptedReloadEpochs = (
  storage: Pick<Storage, 'length' | 'key'>,
): number[] => {
  const epochs: number[] = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (!key?.startsWith(storageUpdateReloadPrefix)) {
      continue;
    }
    const epoch = Number(key.slice(storageUpdateReloadPrefix.length));
    if (Number.isSafeInteger(epoch) && epoch > 0) {
      epochs.push(epoch);
    }
  }
  return epochs;
};

export const initialStorageUpdateFromSession = (
  currentEpoch: number,
  storage: Pick<Storage, 'length' | 'key'> | undefined,
): StorageUpdateView => {
  if (!storage) {
    return idleStorageUpdate();
  }
  const higher = readAttemptedReloadEpochs(storage).filter(
    (epoch) => epoch > currentEpoch,
  );
  if (higher.length === 0) {
    return idleStorageUpdate();
  }
  return storageUpdateForRequiredEpoch(
    currentEpoch,
    Math.max(...higher),
    true,
  );
};

const blockingListeners = new Set<() => void>();

const readSessionStorage = (): Storage | undefined => {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
};

const mutableUpdateState = reactive<StorageUpdateView>(
  typeof window === 'undefined'
    ? idleStorageUpdate()
    : initialStorageUpdateFromSession(
        tabStorageRuntimeEpoch(),
        readSessionStorage(),
      ),
);

const notifyBlockingListeners = () => {
  for (const listener of blockingListeners) {
    listener();
  }
};

const applyView = (view: StorageUpdateView) => {
  const wasBlocking = isBlockingStorageUpdatePhase(mutableUpdateState.phase);
  mutableUpdateState.phase = view.phase;
  mutableUpdateState.requiredEpoch = view.requiredEpoch;
  mutableUpdateState.title = view.title;
  mutableUpdateState.message = view.message;
  if (isBlockingStorageUpdatePhase(view.phase) && !wasBlocking) {
    notifyBlockingListeners();
  }
};

export const isBlockingStorageUpdate = () =>
  isBlockingStorageUpdatePhase(mutableUpdateState.phase);

export const onBlockingStorageUpdate = (listener: () => void) => {
  blockingListeners.add(listener);
  if (isBlockingStorageUpdate()) {
    listener();
  }
  return () => blockingListeners.delete(listener);
};

export const applyStorageUpdateRequired = (requiredEpoch: number) => {
  const currentEpoch = tabStorageRuntimeEpoch();
  const attempted = readAttemptedReloadEpochs(readSessionStorage() ?? {
    length: 0,
    key: () => null,
  }).includes(requiredEpoch);
  const view = storageUpdateForRequiredEpoch(
    currentEpoch,
    requiredEpoch,
    attempted,
  );
  if (view.phase === 'idle') {
    return;
  }
  applyView(view);
};

export const markStorageUpdateReloadAttempt = () => {
  const requiredEpoch = mutableUpdateState.requiredEpoch;
  if (requiredEpoch === undefined) {
    return;
  }
  try {
    window.sessionStorage.setItem(storageUpdateReloadKey(requiredEpoch), '1');
  } catch {
    // sessionStorage can be unavailable in hardened contexts.
  }
};

const handleBroadcast = (message: LocalStorageBroadcastMessage) => {
  if (message.type !== 'storage-update-required') {
    return;
  }
  applyStorageUpdateRequired(message.requiredEpoch);
};

let updateListenerStarted = false;

export const startStorageUpdateListener = () => {
  if (updateListenerStarted || typeof BroadcastChannel === 'undefined') {
    return;
  }
  updateListenerStarted = true;
  const channel = new BroadcastChannel(localStorageBroadcastChannelName);
  channel.onmessage = (event: MessageEvent<LocalStorageBroadcastMessage>) => {
    handleBroadcast(event.data);
  };
};

export const storageUpdateState = readonly(mutableUpdateState);

startStorageUpdateListener();
